import { publicApi } from '@/services/api/AxiosPublicService'

// ============================================================================
// Types
// ============================================================================

export interface SalesLinkSeller {
  id: string
  name: string
  slug: string
  whatsapp: string | null
  email: string | null
  type: 'VENDEDOR' | 'AFILIADO'
  /** Localidade do parceiro (cidade/UF). */
  city: string | null
  uf: string | null
}

/** Regras de elegibilidade resolvidas a partir do PRODUTO do plano. */
export interface PlanEligibilityDegree {
  degreeRelationshipId: string
  degreeRelationship: string | null
  minAge: number | null
  maxAge: number | null
}
export interface PlanEligibility {
  acceptsDependents: boolean
  holder: { minAge: number | null; maxAge: number | null }
  degrees: PlanEligibilityDegree[]
}

export interface SalesLinkPlan {
  id: string
  name: string
  price: number
  priceInCents: number
  planType: 'PESSOA FÍSICA' | 'PESSOA JURÍDICA'
  status: 'ATIVO' | 'INATIVO'
  displayWebsite: boolean

  // Página de vendas (alimentado pelo CRUD em /plans)
  lpOrder:        number | null
  lpName:         string | null
  lpImagePath:    string | null
  lpImageUrl:     string | null
  lpContractPath: string | null
  lpContractUrl:  string | null
  lpCoverage:     string | null
  /** Titular / abrangência. Backend devolve em `lpHolder`. */
  lpHolder:       string | null
  lpBenefits:     string[] | null
  /** Observações exibidas no rodapé do card (texto pequeno). */
  lpNotes:        string | null

  /** Modalidade do plano (Individual, Sênior, Familiar, ) com idade mínima e graus permitidos. */
  planTypeRelationship?: {
    id: string
    name: string
    minAge: number | null
    /** Graus permitidos para dependentes. Vazio = sem restrição. */
    degreeRelationships?: Array<{
      id: string
      degreeRelationship: string
      gender: string | null
    }>
  }

  /** Regras de elegibilidade vindas do produto (idade titular/dep, graus aceitos). */
  eligibility?: PlanEligibility

  formatted?: { price?: string }
}

export interface SalesLinkPayload {
  seller: SalesLinkSeller
  plans: SalesLinkPlan[]
}

export interface LookupState {
  id: string
  state: string
  abbreviation: string
  region: string
}

export interface LookupCity {
  id: string
  city: string
  stateId: string
  state?: LookupState
}

export interface LookupMaritalStatus {
  id: string
  maritalStatus: string
  description: string
}

export interface LookupDegreeRelationship {
  id: string
  code: string
  description: string
}

export interface DiscountCouponPayload {
  id: string
  code: string
  discountType: 'VALOR FIXO' | 'PORCENTAGEM'
  value: number
  monthlyInstallments: number | null
}

export interface DependentPayload {
  name: string
  cpf: string
  birth_date: string
  gender?: 'MASCULINO' | 'FEMININO' | 'OUTRO'
  degree_relationship_id?: string
}

export type SalesPaymentMethod = 'BOLETO' | 'CARTÃO DE CRÉDITO'

export interface SubscribeRequest {
  // Pessoais
  name: string
  social_name?: string
  rnm?: string
  cpf: string
  rg?: string
  birth_date: string
  gender?: 'MASCULINO' | 'FEMININO' | 'OUTRO'
  marital_status_id?: string
  // Contato
  phone?: string
  mobile: string
  business_phone?: string
  email: string
  // Endereço
  postal_code: string
  address: string
  number: string
  complement?: string
  district: string
  city_id: string
  // Plano + pagamento
  plan_id: string
  due_day: number
  payment_method: SalesPaymentMethod
  // Cupom
  discount_coupon_id?: string
  // Dependentes
  dependents?: DependentPayload[]
  // Termos
  terms_accepted: boolean
}

export interface SubscribeResponse {
  subscriberId: string
  sellerId: string
  planId: string
  dependentIds: string[]
}

export interface DoPaymentCard {
  holder_name: string
  number: string
  expiry: string // MM/AA
  cvv: string
}

export interface DoPaymentRequest {
  subscriber_id: string
  payment_method: SalesPaymentMethod
  amount: number
  /** Dados do cartão — só quando CARTÃO DE CRÉDITO. Nunca persistidos. */
  card?: DoPaymentCard
}

export interface DoPaymentResponse {
  revenueId: string
  subscriberId: string
  status: string
  amountPaid: number
  method: SalesPaymentMethod
  /** URL do boleto (quando pagamento por boleto). */
  bankSlipUrl: string | null
}

// ============================================================================
// Service
// ============================================================================

export const SalesLinkService = {
  async show(slug: string): Promise<SalesLinkPayload> {
    const res = await publicApi.get<{ data: SalesLinkPayload }>(`/links/sales/${slug}`)
    return res.data.data
  },

  async subscribe(slug: string, payload: SubscribeRequest): Promise<SubscribeResponse> {
    const res = await publicApi.post<{ data: SubscribeResponse }>(`/links/sales/${slug}/subscribe`, payload)
    return res.data.data
  },

  async doPayment(slug: string, payload: DoPaymentRequest): Promise<DoPaymentResponse> {
    const res = await publicApi.post<{ data: DoPaymentResponse }>(`/links/sales/${slug}/do-payment`, payload)
    return res.data.data
  },

  // -- Lookups públicos -------------------------------------------------------

  async states(): Promise<LookupState[]> {
    const res = await publicApi.get<{ data: LookupState[] }>('/links/locations/states')
    return res.data.data
  },

  async cities(stateId: string): Promise<LookupCity[]> {
    const res = await publicApi.get<{ data: LookupCity[] }>('/links/locations/cities', {
      params: { 'state_id': stateId, size: 0 },
    })
    return res.data.data
  },

  async maritalStatuses(): Promise<LookupMaritalStatus[]> {
    const res = await publicApi.get<{ data: LookupMaritalStatus[] }>('/links/marital-statuses')
    return res.data.data
  },

  async degreeRelationships(): Promise<LookupDegreeRelationship[]> {
    const res = await publicApi.get<{ data: LookupDegreeRelationship[] }>('/links/degree-relationships')
    return res.data.data
  },

  async findDiscountCoupon(code: string, planId: string): Promise<DiscountCouponPayload | null> {
    try {
      const res = await publicApi.get<{ data: DiscountCouponPayload }>('/links/discount-coupons/find', {
        params: { code, 'plan_id': planId },
      })
      return res.data.data
    } catch {
      return null
    }
  },

  // -- CEP via backend (ViaCEP → BrasilAPI fallback) -------------------------

  /**
   * Resolve CEP via endpoint backend, que usa ViaCEP e cai pra BrasilAPI
   * quando o ViaCEP não encontra (corrige CEPs únicos tipo 15895-000).
   * Retorna `null` se nenhum provedor encontrou (ou erro de rede).
   */
  async lookupCep(postalCode: string): Promise<{ address: string; district: string; cityName: string; stateAbbr: string } | null> {
    const cleaned = postalCode.replace(/\D+/g, '')
    if (cleaned.length !== 8) return null
    try {
      const r = await publicApi.get<{ data: { address: string; district: string; cityName: string; stateAbbr: string; source: string } }>(
        `/links/cep/${cleaned}`,
      )
      return r.data.data
    } catch {
      return null
    }
  },
}
